import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileBarChart2, Activity, ChevronRight, TrendingUp, Check } from 'lucide-react';
import styles from './Analytics.module.css';

import { MENU_GROUPS, canSeeItem } from './analyticsData';
import CollectionReport from './reports/CollectionReport/CollectionReport';
import GstReport from './reports/GstReport/GstReport';
import StaffAnalytics from './StaffAnalytics/StaffAnalytics';
import { useAuth } from '../../context/AuthContext';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';
import InventoryAnalytics from './InventoryAnalytics/InventoryAnalytics';

const getLocalTodayString = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MODULE_DETAILS = {
    'sales-analytics': ['Service vs product revenue split', 'Peak business hours', 'Top selling items'],
    'outstanding-analytics': ['Customer-wise dues', 'Khata settlement history', 'Advance wallet use'],
    'staff-analytics': ['Sales by staff, with charts', 'Memberships sold by staff', 'Sales without staff'],
    'inventory-analytics': ['Daily stock in vs out', 'Product sales, profit and margin', 'Reorder list and stock not moving'],
    'service-analytics': ['Most profitable services', 'Underperforming categories', 'Combo package success rate'],
    'collection-report': ['Cash, UPI and card collections', 'Payment mode split', 'Advance adjusted in bills'],
    'billing-report': ['All invoices', 'Cancelled bills', 'Discount usage'],
    'gst-report': ['B2B vs B2C', 'CGST / SGST payable', 'CA-ready exports'],
    'expense-report': ['Petty cash outflows', 'Vendor payments', 'Monthly overheads'],
    'staff-payout-report': ['Service commissions', 'Product sale incentives', 'Final salary settlement'],
};

const OverviewPanel = ({ navigate, allowedMenuGroups }) => (
    <div className={styles.overviewWrapper}>
        <div className={styles.overviewHero}>
            <div className={styles.heroContent}>
                <h1 className={styles.heroTitle}>Business Intelligence Center</h1>
                <p className={styles.heroSubtitle}>Sales, staff, stock and tax reports in one place. Every bill is counted on the day it was completed.</p>
            </div>
            <div className={styles.heroGraphic}><TrendingUp size={64} color="var(--h-color-primary-300)" opacity={0.3} /></div>
        </div>

        <div className={styles.overviewGridContainer}>
            {allowedMenuGroups.map((group) => (
                <div key={group.label} className={styles.groupSection}>
                    <h3 className={styles.groupHeading}>{group.label}</h3>
                    <div className={styles.cardsGrid}>
                        {group.items.map((item) => {
                            const Icon = item.icon;
                            const details = MODULE_DETAILS[item.id] || ['Detailed insights inside'];
                            return (
                                <div
                                    key={item.id}
                                    className={styles.featureCard}
                                    onClick={() => navigate(`/analytics/${item.id}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/analytics/${item.id}`); } }}
                                >
                                    <div className={styles.cardHeader}>
                                        <div className={`${styles.iconBox} ${item.kind === 'report' ? styles.iconReport : styles.iconAnalytic}`}><Icon size={14} strokeWidth={2.5} /></div>
                                        <ChevronRight size={13} className={styles.cardArrow} />
                                    </div>
                                    <div className={styles.cardBody}>
                                        <h4>{item.title}</h4>
                                        <ul className={styles.detailList}>
                                            {details.map((d) => <li key={d}><Check size={10} className={styles.checkIcon} /> {d}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default function Analytics() {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const { hasPermission, user } = useAuth();

    const activeReport = reportId || 'overview';

    // Shared date filter for every report
    const [dateFilter, setDateFilter] = useState(() => ({
        startDate: getLocalTodayString(),
        endDate: getLocalTodayString(),
        label: 'Today',
        value: 'today',
    }));

    const allowedMenuGroups = useMemo(() => MENU_GROUPS
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => user?.role === 'SUPERADMIN' || canSeeItem(item, hasPermission)),
        }))
        .filter((group) => group.items.length > 0), [hasPermission, user?.role]);

    const allAllowedItems = useMemo(() => allowedMenuGroups.flatMap((g) => g.items), [allowedMenuGroups]);
    const overviewItem = { id: 'overview', title: 'Business Overview', icon: Activity, kind: 'analytics' };
    const activeItem = activeReport === 'overview' ? overviewItem : allAllowedItems.find((item) => item.id === activeReport);

    // Unknown report or no access: go back to the overview (outside render)
    useEffect(() => {
        if (activeReport !== 'overview' && !activeItem) navigate('/analytics', { replace: true });
    }, [activeReport, activeItem, navigate]);

    const current = activeItem || overviewItem;
    const ActiveIcon = current.icon || FileBarChart2;

    const renderActiveContent = () => {
        switch (activeItem ? activeReport : 'overview') {
            case 'overview': return <OverviewPanel navigate={navigate} allowedMenuGroups={allowedMenuGroups} />;
            case 'collection-report': return <CollectionReport dateFilter={dateFilter} />;
            case 'gst-report':
            case 'gst-output-report': return <GstReport dateFilter={dateFilter} />;
            case 'staff-analytics': return <StaffAnalytics dateFilter={dateFilter} />;
            case 'inventory-analytics': return <InventoryAnalytics dateFilter={dateFilter} />;
            default:
                return (
                    <div className={styles.emptyModuleWrapper}>
                        <div className={styles.emptyModuleContent}>
                            <ActiveIcon size={32} className={styles.emptyIcon} />
                            <h3>{current.title}</h3>
                            <p>This report is being built and will be available soon.</p>
                            <button type="button" className={styles.goBackBtn} onClick={() => navigate('/analytics')}>Go to Overview</button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <main className={styles.page}>
            <div className={styles.workspace}>
                <aside className={styles.sidebar}>
                    <div className={styles.sidebarHead}><span className={styles.sidebarIcon}><FileBarChart2 size={13} /></span></div>
                    <nav className={styles.reportNav} aria-label="Analytics and reports">
                        <div className={styles.navGroup} style={{ marginBottom: '12px' }}>
                            <button type="button" className={`${styles.navItem} ${activeReport === 'overview' ? styles.activeNavItem : ''}`} onClick={() => navigate('/analytics')}>
                                <span className={styles.navItemIcon}><Activity size={13} /></span>
                                <span className={styles.navItemCopy}><strong>Overview</strong></span>
                                {activeReport === 'overview' && <span className={styles.navActiveMark} />}
                            </button>
                        </div>
                        {allowedMenuGroups.map((group) => (
                            <div className={styles.navGroup} key={group.label}>
                                <span className={styles.navLabel}>{group.label}</span>
                                {group.items.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = activeReport === item.id;
                                    return (
                                        <button type="button" key={item.id} className={`${styles.navItem} ${isActive ? styles.activeNavItem : ''}`} onClick={() => navigate(`/analytics/${item.id}`)}>
                                            <span className={styles.navItemIcon}><Icon size={13} /></span>
                                            <span className={styles.navItemCopy}><strong>{item.title}</strong></span>
                                            {isActive && <span className={styles.navActiveMark} />}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </nav>
                </aside>

                <section className={styles.contentArea}>
                    {activeReport !== 'overview' && activeItem && (
                        <div className={styles.compactActionBar}>
                            <div className={styles.actionLeft}>
                                <ActiveIcon size={14} className={styles.actionIcon} />
                                <div><h2 className={styles.actionTitle}>{current.title}</h2></div>
                            </div>
                            <div className={styles.actionRight}>
                                <GlobalDateFilter startDate={dateFilter.startDate} endDate={dateFilter.endDate} onChange={setDateFilter} defaultLabel={dateFilter.label || 'Today'} />
                            </div>
                        </div>
                    )}
                    <div className={styles.contentScroll}>{renderActiveContent()}</div>
                </section>
            </div>
        </main>
    );
}